import { OutlinedTextFieldProps } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import { ReactComponent as CalendarIcon } from "components/Icons/calendar.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import dayjs, { Dayjs } from "dayjs";
import { Field, getIn } from "formik";
import { useMessage } from "hooks/intl";
import _, { kebabCase } from "lodash";
import { OLDate } from "utilities/dateFormat";
import { inputStyles } from "./styles";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    input: {
      maxWidth: "100%",
      width: ({ width }: { width?: string }) => width || 373,
      ...inputStyles,
    },
    clearIcon: {
      position: "absolute",
      top: "50%",
      right: "0.5rem",
      zIndex: 1,
      cursor: "pointer",
      color: theme.gray500,
      transform: "translateY(-50%)",
    },
    iconWrapper: {
      position: "relative",
      width: "100%",
    },
  }),
);
type Props = Omit<OutlinedTextFieldProps, "variant">;
interface DateTimeInputProps extends Props {
  "data-test-id"?: string;
  displayFormat?: string;
  format?: string;
  size?: "medium" | "small";
  width?: string;
  withClear?: boolean;
  disablePast?: boolean;
  onChange?: (value: any) => void;
  validateOnChange?: boolean;
  onAccept?: () => void;
}

export function DateTimeInput(props: DateTimeInputProps) {
  const classes = useStyles({ width: props.width });
  const { message } = useMessage();
  const { displayFormat, ...passedProps } = props;

  return (
    <Field name={props.name}>
      {({ field, form, meta }: { field: any; form: any; meta: any }) => {
        const { setFieldValue } = form;
        const { value, name } = field;

        return (
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            localeText={{ okButtonLabel: message({ id: "save" }) }}
          >
            <DateTimePicker
              {...field}
              {...passedProps}
              closeOnSelect={false}
              value={value ? dayjs(new Date(value)) : null}
              slots={{ openPickerIcon: CalendarIcon }}
              slotProps={{
                openPickerIcon: {
                  "data-test-id": `${props["data-test-id"]}-icon`,
                },
                actionBar: {
                  actions: ["cancel", "accept"],
                },
                field: { clearable: props.withClear },
                toolbar: {
                  toolbarTitle: passedProps.label,
                },
                textField: {
                  error: !!(
                    meta.error ||
                    props.error ||
                    getIn(form.status, name)
                  ),
                  helperText:
                    meta.error ||
                    (_.isArray(getIn(form.status, name))
                      ? getIn(form.status, name)
                      : props.helperText) ||
                    "",
                  "data-test-id": `${props["data-test-id"]}-${kebabCase(
                    props.name,
                  )}-input`,
                  placeholder: props.placeholder || "",
                  size: props.size,
                },
                day: (props: PickersDayProps<Dayjs>) => {
                  const day = props.day.date();

                  return { "data-test-id": `day-${day}` };
                },
              }}
              disableHighlightToday
              className={`${classes.input} ${props.className}`}
              format={props.displayFormat || "DD MMM, YYYY hh:mm a"}
              inputVariant="outlined"
              sx={{
                "& .MuiPickersToolbar-penIconButton": { display: "none" },
              }}
              onChange={(e) => {
                if (e === null) {
                  setFieldValue(name, null);
                }

                if (props?.onChange) {
                  props.onChange(e);
                } else {
                  if (props.name && e) setFieldValue(props.name, e);
                  if (props.name && e) {
                    setFieldValue(
                      props.name,
                      OLDate(e as any, {
                        formatString: props.format || "dd MMM, yyyy hh:mm a z",
                      }),
                    );
                  }
                }
              }}
              onAccept={() => {
                if (props.validateOnChange) {
                  form.validateField(field.name);
                }
                props.onAccept && props.onAccept();
              }}
            />
          </LocalizationProvider>
        );
      }}
    </Field>
  );
}
